import type { HotEditElementKind } from './elementKind';
import type { HotEditListContext } from './listContext';

/** Prefix for all hot-edit postMessage envelopes between host and preview iframe. */
export const IDEOZ_EDIT_MSG_PREFIX = 'IDEOZ_EDIT_' as const;

export const IDEOZ_PREVIEW_BRIDGE_VERSION = 4 as const;

/** Messages host → iframe */
export type HostToIframeEditMessage =
  | { type: 'IDEOZ_EDIT_ENABLE'; enabled: boolean }
  | { type: 'IDEOZ_EDIT_PING'; requestId: string }
  /** A picture of one element, for the founder to see what they are asking Ideoz about */
  | { type: 'IDEOZ_EDIT_SNAPSHOT'; requestId: string; elementId: string }
  | {
      type: 'IDEOZ_EDIT_APPLY';
      elementId: string;
      patch: StylePatch;
    }
  | {
      type: 'IDEOZ_EDIT_CLEAR_INLINE';
      elementId: string;
      keys?: string[];
    }
  /** Host → iframe: pointer left preview bounds — drop stale hover outline */
  | {
      /**
       * One of the prototype's design system colours, shown live in the whole preview: every
       * `bg-primary` follows it at once, which is what "everywhere" means. `value: null` puts
       * the stylesheet's own colour back.
       */
      type: 'IDEOZ_EDIT_SET_TOKEN';
      token: string;
      value: string | null;
    }
  | { type: 'IDEOZ_EDIT_CLEAR_HOVER' }
  /** Host → iframe: select an element by id (a breadcrumb step) */
  | { type: 'IDEOZ_EDIT_SELECT_ELEMENT'; elementId: string }
  /** Host → iframe: select the parent of the selection (arrow-up) */
  | { type: 'IDEOZ_EDIT_SELECT_PARENT' }
  /** Host → iframe: clear the selection (Esc) */
  | { type: 'IDEOZ_EDIT_DESELECT' }
  /** Host → iframe: select the list row rendered with this React key (a data-grid row) */
  | { type: 'IDEOZ_EDIT_SELECT_BY_KEY'; key: string }
  /** A record's cell shown in the preview before the data file it comes from is written. */
  | {
      type: 'IDEOZ_EDIT_DATA_CELL';
      rowKey: string;
      oldValue: string;
      newValue: string;
      /** Which column, for a cell with nothing written in it yet to be found by. */
      cellIndex?: number;
    }
  /** A record added, copied, removed or moved, shown the same way. */
  | {
      type: 'IDEOZ_EDIT_DATA_ROW';
      op: 'add' | 'duplicate' | 'remove' | 'move';
      rowKey: string;
      newKey?: string;
      toIndex?: number;
      values?: { from: string; to: string }[];
    }
  /** Host → iframe: after the page reloaded, select the element at this path again, under the same id */
  | { type: 'IDEOZ_EDIT_RESELECT'; selectorPath: string; elementId: string };

/** Messages iframe → host */
export type IframeToHostEditMessage =
  | {
      type: 'IDEOZ_EDIT_READY';
      pathname: string;
      version?: number;
    }
  | { type: 'IDEOZ_EDIT_PONG'; requestId: string }
  | {
      type: 'IDEOZ_EDIT_HOVER';
      elementId: string | null;
      rect: DOMRectLike | null;
      tagName: string | null;
    }
  | {
      type: 'IDEOZ_EDIT_SELECT';
      elementId: string;
      rect: DOMRectLike;
      tagName: string;
      /** PascalCase React component owning this DOM node (e.g. CardTitle), when hit target is DS output */
      ownerComponentName?: string;
      source?: ElementDebugSource | null;
      /** Deepest JSX source (often component implementation — for debugging only). */
      implementationSource?: ElementDebugSource | null;
      selectorPath?: string;
      /** When selection is inside a repeated template (`.map`), disambiguates which instance to patch */
      listContext?: HotEditListContext | null;
      /** The repeated row the element belongs to (key from the row itself), or null */
      listRow?: { key: string | null; index: number; count: number; isRowRoot: boolean } | null;
      /** Set when the element itself holds a repeated list */
      listContainer?: { count: number; firstKey: string | null } | null;
      /** What Chef marked this element as: text | surface | list | record | image */
      editMark?: string | null;
      /** The prototype was built with edit marks; without them the editor falls back to its own rules */
      hasEditMarks?: boolean;
      /** The page frame (shell, sidebar, top bar): not edited from the panel */
      isShell?: boolean;
      /** How many elements this one holds */
      childCount?: number;
      /** Component chain from the page down to the element, design-system components skipped */
      breadcrumb?: { label: string; elementId: string }[];
      /** The prototype's CSS color variables (`primary` -> `222.2 47.4% 11.2%`) */
      themeTokens?: Record<string, string>;
      computedStyles: ComputedStylePayload;
      textContent: string;
      className: string;
      /** True when element has element children (complex DOM tree); text edits risky */
      hasChildElements: boolean;
      elementKind?: HotEditElementKind;
      editTargetKind?: 'callSite' | 'designSystemOnly' | 'missing';
      canPersistClasses?: boolean;
      blockReason?: string | null;
      inputPlaceholder?: string;
      inputValue?: string;
      inputType?: string;
      optionValues?: string[];
      /** An <img>'s `src` as the preview shows it (a data: URI for a stored asset) */
      imageSrc?: string;
      /** The founder hid this element; the editor shows it dimmed and offers Unhide */
      isHidden?: boolean;
    }
  /** The founder typed the element's words in the preview itself and kept them (Enter, or blur) */
  | { type: 'IDEOZ_EDIT_TEXT_INPUT'; elementId: string; text: string }
  /** The picture asked for, as a PNG data URL, or null when the element could not be drawn */
  | { type: 'IDEOZ_EDIT_SNAPSHOT_RESULT'; requestId: string; dataUrl: string | null; width: number; height: number }
  /** The selection's box moved (scroll, resize, reflow): the host keeps its toolbar on it */
  | { type: 'IDEOZ_EDIT_RECT'; elementId: string; rect: DOMRectLike }
  | { type: 'IDEOZ_EDIT_ROUTE'; pathname: string }
  | { type: 'IDEOZ_EDIT_DESELECT' }
  | { type: 'IDEOZ_EDIT_HMR_IDLE'; pathname: string }
  /**
   * `src/ideoz/renderSignal.ts`: `#root` went from empty to having elements.
   * `templatePlaceholder`: what rendered is the starter `App.tsx`, not a generated prototype.
   */
  | { type: 'IDEOZ_EDIT_RENDERED'; pathname: string; templatePlaceholder?: boolean }
  /** `src/ideoz/renderSignal.ts`: an error left `#root` empty, or Vite failed to compile. */
  | {
      type: 'IDEOZ_EDIT_CRASHED';
      kind: PreviewCrashKind;
      message: string;
      stack?: string;
      file?: string;
      pathname: string;
    };

/** `runtime`: the app threw and nothing is rendered. `compile`: Vite could not transform a module. */
export type PreviewCrashKind = 'runtime' | 'compile';

export type DOMRectLike = {
  top: number;
  left: number;
  width: number;
  height: number;
  bottom: number;
  right: number;
};

export type ElementDebugSource = {
  filePath: string;
  lineNumber: number;
  columnNumber: number;
};

export type StylePatch =
  | { kind: 'style'; property: string; value: string }
  | { kind: 'className'; add?: string[]; remove?: string[] }
  | { kind: 'text'; value: string }
  | { kind: 'dom'; key: string; value: string }
  /** The paths of an icon the founder picked, drawn into the preview before Apply. */
  | { kind: 'icon'; name: string; markup: string };

/** Snapshot of computed styles we expose to the Tweaks panel (camelCase CSS keys). */
export type ComputedStylePayload = {
  fontFamily?: string;
  fontSize?: string;
  fontWeight?: string;
  color?: string;
  textAlign?: string;
  lineHeight?: string;
  letterSpacing?: string;
  width?: string;
  height?: string;
  opacity?: string;
  backgroundColor?: string;
  gap?: string;
  flexDirection?: string;
  justifyContent?: string;
  alignItems?: string;
  display?: string;
  objectFit?: string;
  /** SVG / icon approximate colors */
  fill?: string;
  stroke?: string;
  paddingTop?: string;
  paddingRight?: string;
  paddingBottom?: string;
  paddingLeft?: string;
  marginTop?: string;
  marginRight?: string;
  marginBottom?: string;
  marginLeft?: string;
  borderTopWidth?: string;
  borderRightWidth?: string;
  borderBottomWidth?: string;
  borderLeftWidth?: string;
  borderStyle?: string;
  borderTopColor?: string;
  borderRadius?: string;
};

export type UnknownEditEnvelope =
  | HostToIframeEditMessage
  | IframeToHostEditMessage
  | Record<string, unknown>;

export function isIdeozEditMessage(data: unknown): data is { type: string } & Record<string, unknown> {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    typeof (data as { type: unknown }).type === 'string' &&
    String((data as { type: string }).type).startsWith(IDEOZ_EDIT_MSG_PREFIX)
  );
}
